import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Form, Input, Switch, Select, message, Table, Space, Popconfirm, Modal, Tag, Row, Col } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title } = Typography;

const AdminStaff = ({ setView }) => {
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    fetchAdminNavItems();
    fetchStaffList();
  }, []);

  const fetchAdminNavItems = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/nav-items/`);
      const data = await response.json();
      if (data.success || data.data) {
        const allItems = data.data || [];
        const adminItems = allItems.filter(item => item.source === 'admin');
        setNavItems(adminItems);
      }
    } catch (error) {
      console.error('Error fetching nav items:', error);
    }
  };

  const fetchStaffList = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/auth/staff/`);
      const data = await response.json();
      // Assume the API returns an array or an object with data property
      if (Array.isArray(data)) {
        setStaffList(data);
      } else if (data.data && Array.isArray(data.data)) {
        setStaffList(data.data);
      } else {
        setStaffList([]);
      }
    } catch (error) {
      console.error('Error fetching staff list:', error);
      message.error('Failed to fetch staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingStaff(null);
    form.resetFields();
    form.setFieldsValue({
      role: 'admin',
      status: true
    });
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    setEditingStaff(record);
    // Fetch full details if needed, or use record if it has all data including nav_items
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/auth/staff/${record.staff_id || record.id}/`);
      const data = await response.json();
      const staffDetails = data.data || data;

      form.setFieldsValue({
        username: staffDetails.username,
        email: staffDetails.email,
        mobile_number: staffDetails.mobile_number,
        role: staffDetails.role,
        status: staffDetails.status,
        nav_items: staffDetails.nav_items || []
      });
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching staff details:', error);
      message.error('Failed to load staff details');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/auth/staff/${id}/`, {
        method: 'DELETE',
      });
      if (response.ok) {
        message.success('Staff member deleted successfully');
        fetchStaffList();
      } else {
        message.error('Failed to delete staff member');
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
      message.error('An error occurred while deleting');
    }
  };

  const handleModalSubmit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        role: values.role || 'admin',
        login_method: 'PASSWORD',
        status: values.status !== undefined ? values.status : true,
        nav_items: values.nav_items || []
      };

      // If editing and password is left blank, don't send it in payload
      if (editingStaff && !payload.password) {
        delete payload.password;
      }

      const url = editingStaff
        ? `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/auth/staff/${editingStaff.staff_id || editingStaff.id}/`
        : `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/auth/add-staff/`;

      const method = editingStaff ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok || data.success || (data.id && data.username)) {
        message.success(`Staff ${editingStaff ? 'updated' : 'added'} successfully!`);
        setIsModalVisible(false);
        fetchStaffList();
      } else {
        message.error(data.message || `Failed to ${editingStaff ? 'update' : 'add'} staff`);
      }
    } catch (error) {
      console.error(`Error ${editingStaff ? 'updating' : 'adding'} staff:`, error);
      message.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Mobile', dataIndex: 'mobile_number', key: 'mobile_number' },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color="blue" style={{ textTransform: 'capitalize' }}>{role}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>{status ? 'ACTIVE' : 'INACTIVE'}</Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="primary" icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this staff member?"
            onConfirm={() => handleDelete(record.staff_id || record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <Title level={2} style={{ color: '#2c3e50', margin: 0 }}>
          Staff Management
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Staff
          </Button>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setView('menu')}>
            Back
          </Button>
        </div>
      </div>

      <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>
        <Table
          columns={columns}
          dataSource={staffList}
          rowKey={(record) => record.staff_id || record.id}
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingStaff ? "Edit Staff Member" : "Add New Staff"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Please enter username' }]}>
                <Input placeholder="e.g. John Doe" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                <Input placeholder="e.g. john@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="mobile_number" label="Mobile Number" rules={[{ required: true, message: 'Please enter mobile number' }]}>
                <Input placeholder="e.g. 9876543210" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: !editingStaff, message: 'Please enter password' }]}
                extra={editingStaff ? "Leave blank to keep existing password" : ""}
              >
                <Input.Password placeholder="Enter secure password" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Please enter a role' }]}>
                <Input placeholder="e.g. admin" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="nav_items" label="Accessible Nav Items" rules={[{ required: true, message: 'Please select at least one nav item' }]}>
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Please select access areas"
                  options={navItems.map(item => ({
                    label: `${item.nav_name} ${item.sub_nav ? `(${item.sub_nav})` : ''}`,
                    value: item.id
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="status" label="Account Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminStaff;
